import React from 'react';
import {
  addTime,
  formatDate,
  isBefore,
  isBetween,
  today,
  endOfMonth,
} from '@perch-labs/warbler';
import { FlexCrate, Text, TextProps } from '@perch-labs/shrike';
import { copyFor } from 'config';
import { paymentMethodTypes, DECLINED_STATUS } from 'domain/payment-method';
import cashappIcon from '/images/payment-method/cashapp.svg';
import achIcon from '/images/payment-method/ach.svg';
import visaIcon from '/images/payment-method/visa.svg';
import mastercardIcon from '/images/payment-method/mastercard.svg';
import amexIcon from '/images/payment-method/amex.svg';
import discoverIcon from '/images/payment-method/discover.png';
import { StyledSVG } from './payment-methods.style';

const getPaymentMethodCopy = copyFor(
  'settings.paymentInformation.paymentMethods'
);

export type PaymentAccount = GetAccountPaymentMethodsQuery['account'];
export type PaymentMethod = PaymentAccount['paymentMethods'][number];
export type PaymentPlan =
  GetPaymentSettingsInfoQuery['search']['plans']['plans'][number];
export type PlanPaymentMethod = PaymentPlan['paymentMethod'];
export type PlanStatement = PaymentPlan['mostRecentStatement'];

interface PaymentCopy {
  label: React.ReactNode;
  text: React.ReactNode;
}

interface PaymentErrorProps {
  paymentMethod: PaymentMethod;
  version?: 'long' | 'short';
}

export interface BasicPaymentMethod {
  id: number;
  expirationDate: string | null;
  source: string;
  type: string;
  description: string;
}
interface PaymentCopyProps {
  paymentMethod: BasicPaymentMethod;
  labelProps?: TextProps;
}

interface IconProps {
  source: string;
  type: string;
}

const isDeclined = (paymentMethod: PaymentMethod): boolean => {
  return paymentMethod.mostRecentPaymentTransaction?.status === DECLINED_STATUS;
};

export const isExpired = (paymentMethod: BasicPaymentMethod): boolean => {
  const { expirationDate } = paymentMethod || {};
  const fullExpiration = endOfMonth(expirationDate || '');
  if (!fullExpiration) return false;

  return isBefore(fullExpiration, today());
};

export const isExpiring = (paymentMethod: BasicPaymentMethod): boolean => {
  const { expirationDate } = paymentMethod || {};
  const fullExpiration = endOfMonth(expirationDate || '');
  if (!fullExpiration) return false;
  const dateToday = today();

  return isBetween(fullExpiration, {
    start: dateToday,
    end: addTime(endOfMonth(dateToday), { months: 1 }),
  });
};

export const getPaymentMethodError = ({
  paymentMethod,
  version = 'long',
}: PaymentErrorProps): string | undefined => {
  if (isDeclined(paymentMethod)) {
    return getPaymentMethodCopy(`errors.declineError.${version}`);
  } else if (isExpired(paymentMethod)) {
    return getPaymentMethodCopy(`errors.expiredPaymentMethod.${version}`);
  } else if (isExpiring(paymentMethod)) {
    return getPaymentMethodCopy(`errors.expiringPaymentMethod.${version}`);
  }
};

export const getPaymentMethodIcon = ({
  source,
  type,
}: IconProps): React.ReactNode | null => {
  switch (type) {
    case paymentMethodTypes.creditCard:
      switch (source) {
        case 'Visa':
          return <StyledSVG src={visaIcon} />;
        case 'American Express':
          return <StyledSVG src={amexIcon} />;
        case 'Discover':
          return (
            <img
              height="12px"
              width="24px"
              src={discoverIcon}
              aria-hidden={true}
              alt=""
            />
          );
        case 'MasterCard':
          return <StyledSVG src={mastercardIcon} />;
        default:
          return null;
      }
    case paymentMethodTypes.ach:
      return <StyledSVG src={achIcon} width="16px" margin="0 4px" />;
    case paymentMethodTypes.cashApp:
      return <StyledSVG src={cashappIcon} />;
    default:
      return null;
  }
};

export const getCopy = ({
  paymentMethod,
}: {
  paymentMethod: BasicPaymentMethod;
}) => {
  const { type, description, expirationDate } = paymentMethod;
  switch (type) {
    case paymentMethodTypes.creditCard:
      return {
        labelCopy: getPaymentMethodCopy('creditCard.label', { description }),
        textCopy: getPaymentMethodCopy('creditCard.description', {
          date:
            expirationDate && formatDate(expirationDate, { format: 'MM/yy' }),
        }),
      };
    case paymentMethodTypes.ach:
      return {
        labelCopy: getPaymentMethodCopy('ach.label', { description }),
        textCopy: getPaymentMethodCopy('ach.description'),
      };
    case paymentMethodTypes.cashApp:
      return {
        labelCopy: getPaymentMethodCopy('cashapp.label'),
        textCopy: getPaymentMethodCopy('cashapp.description'),
      };
    default:
      return {
        labelCopy: getPaymentMethodCopy('other.label', { description }),
        textCopy: getPaymentMethodCopy('other.description'),
      };
  }
};

export const getPaymentCopy = ({
  paymentMethod,
  labelProps,
}: PaymentCopyProps): PaymentCopy => {
  const { type, source } = paymentMethod;
  const icon = getPaymentMethodIcon({ source, type });
  const { labelCopy, textCopy } = getCopy({ paymentMethod });

  return {
    label: (
      <FlexCrate alignItems="center">
        {icon}
        <Text
          tag="h3"
          textStyle="heading500"
          margin={{ left: icon ? '8px' : '32px' }}
          {...labelProps}
        >
          {labelCopy}
        </Text>
      </FlexCrate>
    ),
    text: (
      <Text
        margin={{ left: '32px' }}
        color="secondary"
        textStyle="paragraph400"
      >
        {textCopy}
      </Text>
    ),
  };
};
